import { memo, useRef, useState } from 'react';

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function titleGradient(title: string): string {
  const h = hashStr(title.split('').reverse().join('') || 'Slot');
  const hue1 = h % 360;
  const hue2 = (hue1 + 40 + ((h >> 8) % 80)) % 360;
  return `linear-gradient(145deg, hsl(${hue1},65%,48%), hsl(${hue2},75%,32%))`;
}

// Every game's static root serves a `favicon.png` alongside its
// reelay.html — e.g. ".../games/dampirates/reelay.html#remoteUI" →
// ".../games/dampirates/favicon.png". In dev, each game's own dev server
// has no path segment at all (just "http://localhost:3000/#remoteUI"), so
// the root itself IS the game's static root — appending "favicon.png"
// still lands in the right place. Returns null for a URL that can't be
// parsed at all (callers fall back to the generated initials icon either
// way, via the <img>'s onError).
function faviconUrl(gameUrl: string): string | null {
  try {
    const u = new URL(gameUrl);
    let path = u.pathname;
    if (path.endsWith('.html')) path = path.slice(0, path.lastIndexOf('/') + 1);
    if (!path.endsWith('/')) path += '/';
    return `${u.origin}${path}favicon.png`;
  } catch {
    return null;
  }
}

export interface HistoryItem {
  gameTitle: string;
  url: string;
  timestamp: number;
  spins: number;
}

const DISMISS_THRESHOLD = 80;

// Memoized — lives inside ReelsStack's render tree, which re-renders on
// every swipe-drag rAF frame (dragY/dragging state). Paired with
// ReelsStack now passing a stable `currentItem` (useMemo) and stable
// `onClose`/`onItemClick` (useCallback) so this boundary actually holds.
export const HistoryPanel = memo(function HistoryPanel({
  items,
  currentItem,
  loadingUrl,
  onItemClick,
  onClose,
  onDismissProgress,
}: {
  items: HistoryItem[];
  currentItem?: HistoryItem | null;
  loadingUrl?: string | null;
  onItemClick: (item: HistoryItem) => void;
  onClose: () => void;
  onDismissProgress: (dy: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const activeRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startYRef.current = e.clientY;
    activeRef.current = false;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startYRef.current === null) return;
    const dy = e.clientY - startYRef.current;
    const scrollTop = scrollRef.current?.scrollTop ?? 0;

    if (!activeRef.current) {
      if (dy < -10 && scrollTop === 0) {
        activeRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      } else if (dy > 5 || scrollTop > 0) {
        startYRef.current = null;
      }
      return;
    }

    onDismissProgress(Math.min(dy, 0));
  };

  const finishGesture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startYRef.current === null) return;
    const dy = e.clientY - startYRef.current;
    startYRef.current = null;
    if (!activeRef.current) return;
    activeRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    onDismissProgress(0);
    if (dy < -DISMISS_THRESHOLD) onClose();
  };

  return (
    <div
      ref={scrollRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishGesture}
      onPointerCancel={finishGesture}
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg, #0a0e1a)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        touchAction: 'pan-y',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 18px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          position: 'sticky',
          top: 0,
          background: 'var(--bg, #0a0e1a)',
          zIndex: 1,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text, rgba(255,255,255,0.9))',
            fontSize: 20,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
            flexShrink: 0,
          }}
          aria-label="Back"
        >
          ←
        </button>
        <span
          style={{
            fontSize: 13,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--text, rgba(255,255,255,0.9))',
            fontWeight: 400,
          }}
        >
          Game History
        </span>
      </div>

      {items.length === 0 && !currentItem ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.35)',
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          No games visited yet
        </div>
      ) : (
        items.map((item, i) => (
          <HistoryRow
            key={`${item.timestamp}:${i}`}
            item={item}
            loading={loadingUrl === item.url}
            disabled={loadingUrl !== null && loadingUrl !== item.url}
            onClick={() => onItemClick(item)}
          />
        ))
      )}

      {currentItem && (
        <HistoryRow
          key="current"
          item={currentItem}
          isCurrent
          loading={false}
          disabled={false}
          onClick={() => onItemClick(currentItem)}
        />
      )}
    </div>
  );
});

function HistoryRow({
  item,
  isCurrent = false,
  loading,
  disabled,
  onClick,
}: {
  item: HistoryItem;
  isCurrent?: boolean;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const timeStr = new Date(item.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const initials = (item.gameTitle || 'S')
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  const favicon = faviconUrl(item.url);
  // Falls back to the generated initials tile below if favicon.png 404s
  // for this game, or if the url couldn't be parsed at all — this is
  // asset-optional, not something worth a broken-image icon over.
  const [faviconFailed, setFaviconFailed] = useState(false);
  const showFavicon = favicon && !faviconFailed;

  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 18px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        background: isCurrent ? 'var(--surface-2, rgba(255,255,255,0.08))' : 'var(--surface, rgba(255,255,255,0.04))',
        gap: 14,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {showFavicon ? (
        <img
          src={favicon}
          alt=""
          draggable={false}
          onError={() => setFaviconFailed(true)}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            flexShrink: 0,
            objectFit: 'cover',
            background: 'var(--surface-2, rgba(255,255,255,0.08))',
          }}
        />
      ) : (
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: titleGradient(item.gameTitle),
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            fontWeight: 100,
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.02em',
          }}
        >
          {initials}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: isCurrent ? 700 : 300,
            color: isCurrent ? 'var(--reward, #C7D84A)' : 'var(--text, rgba(255,255,255,0.9))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.gameTitle || 'Slot'}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.4)',
            marginTop: 3,
            letterSpacing: '0.04em',
          }}
        >
          {timeStr}
        </div>
      </div>
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 2,
        }}
      >
        <span
          style={{
            fontSize: 18,
            fontWeight: 400,
            color: 'var(--reward, #C7D84A)',
            letterSpacing: '0.02em',
          }}
        >
          {item.spins}
        </span>
        <span
          style={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {item.spins === 1 ? 'spin' : 'spins'}
        </span>
      </div>
      {loading && <RowSpinner />}
    </div>
  );
}

function RowSpinner() {
  return (
    <svg
      className="reelay-spinner"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M8 2 a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
